import { NOT_CALLABLE_REASONS, type NotCallableReasonCode } from "@gioia365/contracts";
import { ApiRequestError } from "../api/http";
import { messageForError } from "../i18n/errors";
import type { Translate } from "../i18n/locale";
import type { CallSessionError } from "./call-session-state";

// Le parole dei motivi e degli errori delle chiamate (piano M4, Task 13). «Chiama» spento nella scheda e il rifiuto del
// server dicono lo stesso motivo con le stesse parole (decisione del controllore sul Task 8): `callDesk.reasons.*`, uno per
// ogni `NotCallableReason`. Gli altri rifiuti del server hanno il loro testo in `errors.*`.

const REASONS: ReadonlySet<string> = new Set(NOT_CALLABLE_REASONS);

function isReason(value: unknown): value is NotCallableReasonCode {
  return typeof value === "string" && REASONS.has(value);
}

/**
 * Perché «Chiama» è spento, con il nome del collega al telefono o che lavora il Lead nel Power Dialer. Un unico testo per
 * motivo, sulla scheda e nel rifiuto del server; `mine` (la chiamata aperta è di chi guarda) dice `callDesk.inCallMine`.
 */
export function reasonText(t: Translate, reason: NotCallableReasonCode, byDisplayName: string | null, mine = false): string {
  if (reason === "in_call" && mine) return t("callDesk.inCallMine");
  return t(`callDesk.reasons.${reason}`, { name: byDisplayName ?? "" });
}

/** I rifiuti del server che portano un motivo della regola (`details.reason`): si dicono con le parole della scheda. */
const REFUSALS_WITH_REASON: ReadonlySet<string> = new Set(["lead_not_callable", "permission_denied", "telephony_unavailable", "caller_number_missing"]);

/**
 * Che cosa dire quando la chiamata non parte: il telefono del browser (microfono, SDK, fornitore), la chiamata finita senza
 * arrivare al Lead o il rifiuto del server. Un rifiuto con un motivo della regola (`lead_not_callable`, `permission_denied`,
 * `telephony_unavailable`, `caller_number_missing` della persona senza cellulare, Task 16) dice quel motivo, con le parole
 * della scheda; `lead_in_call` e `lead_reserved` anche il nome.
 */
export function callErrorText(t: Translate, error: CallSessionError): string {
  if (!(error instanceof ApiRequestError)) {
    switch (error.kind) {
      case "closed_early": return t("callDesk.closedEarly");
      case "hangup_unmeasured": return t("callDesk.hangupUnmeasured");
      case "closure_unconfirmed": return t("callDesk.closureUnconfirmed");
      case "not_connected":
        return t("callDesk.notConnected");
      case "call_gone":
        return t("callDesk.callGone");
      case "microphone":
        return t("callDesk.microphone");
      case "load":
        return t("callDesk.load");
      case "twilio":
        return error.code === null ? t("callDesk.notConnected") : t("callDesk.providerError", { code: String(error.code) });
    }
  }
  const details = error.details ?? {};
  const name = typeof details.byDisplayName === "string" ? details.byDisplayName : "";
  if (error.code === "lead_in_call") return reasonText(t, "in_call", name, details.mine === true);
  if (error.code === "lead_reserved") return reasonText(t, "reserved", name);
  if (REFUSALS_WITH_REASON.has(error.code) && isReason(details.reason)) return reasonText(t, details.reason, name);
  return messageForError(t, error);
}

/** L'errore che soltanto un ricaricamento risolve: il telefono del browser non si è caricato (di solito, la pagina è di prima di un aggiornamento). */
export function needsReload(error: CallSessionError): boolean {
  return !(error instanceof ApiRequestError) && error.kind === "load";
}
